import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';

// Banner de falha de permissao/conexao com a Meta. Antes o backend caia em
// dados mock quando a Graph recusava a chamada, entao esta tela nunca teve como
// mostrar o problema — este componente e o destino desses erros.
const TONE = {
    // Falta de escopo / token expirado: o usuario resolve reconectando.
    action: {
        box: 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30',
        iconBox: 'bg-amber-100 dark:bg-amber-500/20',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-900 dark:text-amber-200',
        text: 'text-amber-800/90 dark:text-amber-200/80',
        chip: 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-500/30',
        button: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
    // Falha do lado da Meta: nao ha o que o usuario reconecte.
    error: {
        box: 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30',
        iconBox: 'bg-red-100 dark:bg-red-500/20',
        icon: 'text-red-600 dark:text-red-400',
        title: 'text-red-900 dark:text-red-200',
        text: 'text-red-800/90 dark:text-red-200/80',
        chip: 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-500/30',
        button: 'bg-red-600 hover:bg-red-700 text-white',
    },
};

const PLATFORM_ICON = {
    instagram: 'mdi:instagram',
    facebook: 'mdi:facebook',
};

function getKindTitle(t, kind) {
    const titles = {
        MISSING_SCOPE: t('analytics.scope.missingScope'),
        INVALID_TOKEN: t('analytics.scope.invalidToken'),
        UPSTREAM_ERROR: t('analytics.scope.upstreamError'),
    };
    return titles[kind] || t('analytics.scope.loadFailed');
}

export default function MktScopeAlert({ error, onReconnect, onRetry }) {
    const { t } = useTranslation('marketing');

    if (!error) return null;

    const kind = error.kind || 'UPSTREAM_ERROR';
    const needsReconnect = kind === 'MISSING_SCOPE' || kind === 'INVALID_TOKEN';
    const tone = needsReconnect ? TONE.action : TONE.error;
    const scopes = error.missing_scopes || [];

    return (
        <div className={`${tone.box} rounded-xl p-4 mb-4 flex items-start gap-3`}>
            <div className={`${tone.iconBox} w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Iconify
                    icon={PLATFORM_ICON[error.platform] || 'eva:alert-triangle-outline'}
                    width={20}
                    className={tone.icon}
                />
            </div>

            <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${tone.title}`}>
                    {getKindTitle(t, kind)}
                </p>
                <p className={`text-xs mt-0.5 ${tone.text}`}>
                    {error.message || t('analytics.scope.retryHint')}
                </p>

                {scopes.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {scopes.map((scope) => (
                            <span
                                key={scope}
                                className={`${tone.chip} text-[11px] font-mono border px-2 py-0.5 rounded-full`}
                            >
                                {scope}
                            </span>
                        ))}
                    </div>
                )}

                {/* Mensagem crua da Graph: sem ela, "falta permissao" vira adivinhacao no suporte. */}
                {error.graph_message && (
                    <p className={`text-[11px] mt-2 opacity-70 ${tone.text}`}>
                        {t('analytics.scope.graphPrefix')} {error.graph_message}
                    </p>
                )}

                <div className="flex items-center gap-2 mt-3">
                    {needsReconnect && onReconnect && (
                        <button
                            type="button"
                            onClick={onReconnect}
                            className={`${tone.button} text-xs font-medium px-3 py-1.5 rounded-lg transition-colors active:transform active:scale-95`}
                        >
                            {t('analytics.scope.reconnect')}
                        </button>
                    )}
                    {onRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-accent transition-colors"
                        >
                            {t('common.retry')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
